/**
 * Person Lookup Port - DDD Domain's view of person data needs
 * 
 * This port defines what the DDD Domain needs from external person data
 * without being coupled to Party Manager's internal implementation.
 * 
 * Following Hexagonal Architecture (Ports & Adapters) and Eric Evans DDD:
 * - Port = Interface (what DDD Domain needs)
 * - Adapter = Implementation (how to get it from Party Manager)
 * - This port protects DDD Domain's model integrity
 * 
 * The implementation will use Party Manager's Published Language contracts
 * but translate them to DDD's domain language through an Anti-Corruption Layer.
 */

/**
 * Person Reference - DDD Domain's view of a person
 * This is NOT Party Manager's model, but what DDD cares about
 */
export interface PersonReference {
  readonly personId: string;
  readonly displayName: string;
  readonly email: string;
  readonly roleType: 'Human' | 'AiAgent';
  readonly isActive: boolean;
  readonly department?: string;
  readonly jobTitle?: string;
}

/**
 * Port defining person lookup capabilities needed by DDD Domain
 * 
 * This interface is owned by DDD Domain and represents its needs.
 * The implementation (adapter) will translate from Party Manager's Published Language.
 */
export interface PersonLookupPort {
  /**
   * Find a person by their ID
   * Returns null if not found or not accessible
   */
  findPersonById(personId: string): Promise<PersonReference | null>;

  /**
   * Find all persons in a specific department
   * Used for DDD analysis of team structures
   */
  findPersonsByDepartment(department: string): Promise<PersonReference[]>;

  /**
   * Find persons by role type (Human vs AiAgent)
   * Used for analyzing AI adoption in DDD implementations
   */
  findPersonsByRoleType(roleType: 'Human' | 'AiAgent'): Promise<PersonReference[]>;

  /**
   * Search persons by partial name or email
   * Used for finding domain experts and architects
   */
  searchPersons(searchTerm: string, limit?: number): Promise<PersonReference[]>;

  /**
   * Check if person lookup service is available
   * Used for graceful degradation
   */
  isAvailable(): Promise<boolean>;
}

/**
 * DDD Domain specific error for person lookup issues
 */
export class PersonLookupError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
    public readonly operation?: string
  ) {
    super(message);
    this.name = 'PersonLookupError';
  }
}

/**
 * DI Token for Person Lookup Port
 * Used by dependency injection container
 */
export const PERSON_LOOKUP_PORT = Symbol('PersonLookupPort');